import {
  OrderResultsReducerChangeLastPreviewOrderAction,
  OrderResultsReducerNewPreviewOrderPendingAction,
} from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { EMPTY, merge, Observable, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'

import { OrderFailedResult } from 'components/features/rewards/rewardsPane/types/orderResult'
import {
  CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
  CRYPTO_REWARDS_SELL_MODE_STEP_PENDING,
  CRYPTO_REWARDS_SELL_MODE_STEP_PREVIEW_ORDER,
} from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { EpicRefined } from 'interfaces/middleware'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import { CryptoRewardsPaneActions } from 'components/features/rewards/rewardsPane/store/actions'
import { byType } from 'rxOperators/byType'
import {
  createRewardsPaneActionChangeSellModeStepState,
  SuccessCryptoRewardsPaneModeStepPayload,
  SuccessCryptoRewardsPaneSellModeStepAction,
} from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { GET_PREVIEW_ORDER_SELL_CRYPTO_URL } from 'components/features/rewards/rewardsPane/constants/endpoints'
import { ModifyFloatingPane } from 'actions/floatingPanesActions'
import { CryptoRewardsSellCryptoPreviewOrderRequest } from 'components/features/rewards/rewardsPane/types/apiRequests'
import {
  CryptoRewardsSellCryptoPreviewOrderFailedResponse,
  CryptoRewardsSellCryptoPreviewOrderSuccessResponse,
} from 'components/features/rewards/rewardsPane/types/apiResponses'
import { PreviewOrderResult, PreviewOrderResultWithStatus } from 'components/features/rewards/rewardsPane/types/orderPreviewResult'
import { CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR } from 'components/features/rewards/rewardsPane/constants/defaults'
import size from 'lodash/size'

type PaneSellMode = typeof FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE

export const createSellCryptoPreviewOrderEpic: EpicRefined<CryptoRewardsPaneActions | ModifyFloatingPane | AjaxCatchExtraActions> = (
  (action$, _, { get }) => action$
    .pipe(
      byType(SuccessCryptoRewardsPaneSellModeStepAction.type),
      mergeMap(
        ({ payload }) => {
          if (payload.step !== CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT) {
            return EMPTY
          }

          const { fields, backStep }: SuccessCryptoRewardsPaneModeStepPayload<PaneSellMode> = payload

          if (!fields) {
            throw new Error('No order data')
          }

          const {
            cryptoCurrencyIDChosenForSelling,
            sellingAmountUSD,
            sellingAmountCrypto,
          } = fields

          const actionOpenSendingOrderStep: ModifyFloatingPane = (
            createRewardsPaneActionChangeSellModeStepState({
              step: CRYPTO_REWARDS_SELL_MODE_STEP_PENDING,
              progressPercents: 50,
              fields: {
                cryptoCurrencyIDChosenForSelling,
                sellingAmountUSD,
                sellingAmountCrypto,
              },
              backStep,
            })
          )
          const actionOpenSendingOrderStepObservable: Observable<ModifyFloatingPane> = of(actionOpenSendingOrderStep)
          const actionResetLastOrderResultObservable = of(
            new OrderResultsReducerNewPreviewOrderPendingAction()
          )

          const sendOrderParams: CryptoRewardsSellCryptoPreviewOrderRequest = {
            amount_usd: sellingAmountUSD,
            amount_crypto: sellingAmountCrypto,
            ticker: cryptoCurrencyIDChosenForSelling,
          }
          const sendOrderObservable = get(GET_PREVIEW_ORDER_SELL_CRYPTO_URL, sendOrderParams).pipe(
            mergeMap(({ response }) => {
              const {
                amount_usd,
                reward,
                fees,
                taxes,
              }: CryptoRewardsSellCryptoPreviewOrderSuccessResponse = response

              const successResult: PreviewOrderResult = {
                amountToSellUSD: Number(amount_usd),
                cryptoCurrencyId: cryptoCurrencyIDChosenForSelling,
                estRewardsUSD: Number(reward),
                feesUSD: Number(fees),
                taxesUSD: Number(taxes),
              }
              const previewOrderSuccessResult: PreviewOrderResultWithStatus = {
                isPending: false,
                success: successResult,
              }
              const actionSetSuccessPreviewOrderResult = new OrderResultsReducerChangeLastPreviewOrderAction(
                previewOrderSuccessResult
              )
              const actionChangePaneStep = createRewardsPaneActionChangeSellModeStepState({
                step: CRYPTO_REWARDS_SELL_MODE_STEP_PREVIEW_ORDER,
                progressPercents: 50,
                fields,
                backStep,
              })
              return merge(
                of(actionSetSuccessPreviewOrderResult),
                of(actionChangePaneStep),
              )
            }),
            ajaxCatch((error) => {
              const {
                amount_usd,
                ticker,
                non_field_errors,
              }: CryptoRewardsSellCryptoPreviewOrderFailedResponse = Boolean(size(error.response))
                ? error.response
                : CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR
              const fieldsErrors = {
                amount_usd,
                ticker,
              }
              const previewOrderFailedErrors: OrderFailedResult<PaneSellMode> = {
                fieldsErrors: fieldsErrors,
                generalErrors: non_field_errors,
              }
              const previewOrderResultWithStatus: PreviewOrderResultWithStatus = {
                isPending: false,
                errors: previewOrderFailedErrors,
                success: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastPreviewOrderAction(previewOrderResultWithStatus)
              const actionChangePaneStep = createRewardsPaneActionChangeSellModeStepState({
                step: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
                progressPercents: 20,
                fields,
                backStep,
              })
              return merge(
                of(actionSetOrderResult),
                of(actionChangePaneStep),
              )
            })
          )
          return merge(
            actionOpenSendingOrderStepObservable,
            actionResetLastOrderResultObservable,
            sendOrderObservable
          )
        }
      )
    )
)
